import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';

/**
 * One value out of a schema enum — PATH-01.
 *
 * Six selects over six enums, and the copy worth removing is the turn back from MUI's
 * `string` into a member: `subjectSchema.parse(...)` at one call site, something else at
 * the next. Looked up in `options` instead, so the callback is typed and there is no cast.
 *
 * Not `FilterSelect`, which it resembles: there an empty value is *selectable* and means
 * "Any", here it is disabled and means "not set" — the one value a settings field may not
 * take. Merging them needs a prop that flips that meaning.
 */
export function ChoiceField<T extends string>({
  id,
  label,
  value,
  options,
  describe,
  group,
  disabled = false,
  onChange,
}: {
  /** Where a publish refusal about this field jumps to. */
  id?: string;
  label: string;
  /** Undefined for a column nothing has set — the generator leaves several unset. */
  value: T | undefined;
  options: readonly T[];
  /** What a human reads, when the enum value is not it. */
  describe?: (option: T) => string;
  /**
   * Which section an option sits under, for an enum too long to scan flat — VIS-13.
   * Providing it switches the field to a native select; see below. `options` must
   * already be in group order, or a group is headed twice rather than once.
   */
  group?: (option: T) => string;
  disabled?: boolean;
  onChange: (next: T) => void;
}) {
  const text = (option: T): string =>
    describe === undefined ? option : describe(option);

  /**
   * Grouped means native, because `<optgroup>` is the only thing here that is a group.
   *
   * MUI's menu has no notion of one: `SelectInput` clones `role="option"` and a click
   * handler onto every child it is given, with no case for `ListSubheader` and no opt
   * out, so a heading rendered that way is announced and reachable as a choice. The
   * measurement that settled it, and the cost — this one field is drawn by the browser
   * while its neighbours are drawn by MUI — are in
   * `docs/decisions/illustration-object-groups.md`.
   */
  const grouped = group !== undefined;

  const runs: { name: string; options: T[] }[] = [];

  if (group !== undefined) {
    for (const option of options) {
      const name = group(option);
      const last = runs.at(-1);

      if (last?.name === name) last.options.push(option);
      else runs.push({ name, options: [option] });
    }
  }

  const unset = value === undefined;

  return (
    <TextField
      select
      id={id}
      label={label}
      size="small"
      fullWidth
      disabled={disabled}
      value={value ?? ''}
      slotProps={
        grouped
          ? // The browser draws its own select, so the label has nowhere to sit unless
            // it is told to stay up.
            { select: { native: true }, inputLabel: { shrink: true } }
          : {}
      }
      onChange={(event) => {
        const next = options.find((one) => one === event.target.value);

        if (next !== undefined) onChange(next);
      }}
    >
      {/* Only while nothing is set, and never selectable. Without it the field is showing
          a value that is in no option, which MUI warns about; offered as a real choice it
          would propose unsetting a topic, which no endpoint accepts. */}
      {unset &&
        (grouped ? (
          <option value="" disabled>
            Not set
          </option>
        ) : (
          <MenuItem value="" disabled>
            Not set
          </MenuItem>
        ))}

      {grouped
        ? runs.map((run) => (
            <optgroup key={run.name} label={run.name}>
              {run.options.map((option) => (
                <option key={option} value={option}>
                  {text(option)}
                </option>
              ))}
            </optgroup>
          ))
        : options.map((option) => (
            <MenuItem key={option} value={option}>
              {text(option)}
            </MenuItem>
          ))}
    </TextField>
  );
}
